/**
 * P5 — N1 · Competência 2 (Algébrico-funcional) · task-model H1.8.01
 *
 *   "Corresponder a lei de uma função afim ao seu gráfico (e vice-versa)."
 *
 * Estreia a **estrutura E6**: as alternativas não são valores, são FIGURAS. Cada uma
 * é um plano cartesiano gerado a partir de um par (a, b) — e cada par errado vem de
 * um erro de leitura nomeado, exatamente como um distrator numérico viria.
 *
 * Uma decisão de acessibilidade que vale registrar: o texto visível de cada
 * alternativa é VAZIO, e a descrição do gráfico vive no `a11y` da própria figura.
 * Se a descrição fosse texto visível ("reta decrescente que corta o eixo y em 3"),
 * o item passaria a ser respondível sem olhar para gráfico nenhum — o que anularia
 * a estrutura E6. Leitor de tela recebe a descrição; quem enxerga, vê o gráfico.
 */

import { formatBR } from '../../core/num.ts';
import { conta } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { afimScene, describeAfim } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  FUN_ANGULAR_UM,
  FUN_INTERCEPTO_EM_X,
  FUN_PELA_ORIGEM,
  FUN_REFLETE_EIXO_X,
  FUN_SINAL_ANGULAR,
  FUN_TROCA_A_B,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { strong, tex, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

export interface Params {
  /** Coeficiente angular. */
  readonly a: number;
  /** Coeficiente linear. */
  readonly b: number;
  /** Semi-extensão do quadro, em unidades. */
  readonly span: number;
}

/** O par (a, b) é a "resposta": é ele que identifica cada gráfico. */
export interface Reta {
  readonly a: number;
  readonly b: number;
}

const ANGULARES = [-3, -2, -0.5, 0.5, 2, 3] as const;

/**
 * As restrições existem para impedir que dois distratores virem o mesmo gráfico:
 *
 *   - `a ≠ ±1`, senão "assume coeficiente angular 1" reproduz a chave;
 *   - `b ≠ 0`, senão "reta pela origem" reproduz a chave;
 *   - `a ≠ b`, senão "troca a por b" reproduz a chave;
 *   - a reta precisa cruzar o quadro de forma legível nos dois lados.
 */
function drawParams(rng: Rng): Params {
  const span = rng.pick([5, 6]);

  const a = rng.pick(ANGULARES);
  const b = sampleWhere(
    rng.fork('b'),
    (r) => r.int(-4, 4),
    (cand) =>
      cand !== 0 &&
      cand !== a &&
      Math.abs(cand) <= span - 1 &&
      // o intercepto lido no eixo x (-a·b) precisa caber no quadro, senão aquele
      // distrator vira um gráfico vazio
      Math.abs(a * cand) <= span,
    { label: 'b' },
  );

  return { a, b, span };
}

const solveKey = (p: Params): Reta => ({ a: p.a, b: p.b });

const mesmaReta = (x: Reta, y: Reta): boolean =>
  Math.abs(x.a - y.a) < 1e-9 && Math.abs(x.b - y.b) < 1e-9;

/** Rejeita gráficos que sairiam do quadro ou degenerados. */
const cabe = (r: Reta, span: number): boolean =>
  Math.abs(r.a) > 1e-9 && Math.abs(r.b) <= span && Math.abs(r.a) <= 6;

const BUGGY: readonly BuggySolver<Params, Reta>[] = [
  {
    // O erro que dá nome ao task-model: confunde crescente com decrescente.
    error: FUN_SINAL_ANGULAR,
    priority: 1,
    solve: (p) => ({ a: -p.a, b: p.b }),
  },

  // --- faixa 2: sorteados entre si -----------------------------------------
  {
    // Toma o ponto em que a reta cruza o eixo X como se fosse o coeficiente linear:
    // mesma inclinação, mas passando por (b, 0).
    error: FUN_INTERCEPTO_EM_X,
    priority: 2,
    solve: (p) => {
      const r = { a: p.a, b: -p.a * p.b };
      return cabe(r, p.span) ? r : NOT_APPLICABLE;
    },
  },
  {
    error: FUN_ANGULAR_UM,
    priority: 2,
    solve: (p) => ({ a: Math.sign(p.a), b: p.b }),
  },
  {
    error: FUN_PELA_ORIGEM,
    priority: 2,
    solve: (p) => ({ a: p.a, b: 0 }),
  },
  {
    error: FUN_TROCA_A_B,
    priority: 2,
    solve: (p) => {
      const r = { a: p.b, b: p.a };
      return cabe(r, p.span) ? r : NOT_APPLICABLE;
    },
  },

  // --- faixa 3: reposição ---------------------------------------------------
  {
    error: FUN_REFLETE_EIXO_X,
    priority: 3,
    solve: (p) => ({ a: -p.a, b: -p.b }),
  },
];

/**
 * Coeficiente racional em LaTeX, como FRAÇÃO — nunca em decimal.
 *
 * Em matemática escolar brasileira o coeficiente angular de uma reta se escreve
 * `½x` ou `x/2`, não `0,5x`. A forma decimal aparece na conta, não na lei da função;
 * escrevê-la no enunciado ensina uma notação que o livro didático não usa.
 */
function racionalTex(x: number): string {
  const sinal = x < 0 ? '-' : '';
  const m = Math.abs(x);
  if (Number.isInteger(m)) return `${sinal}${m}`;

  for (let d = 2; d <= 6; d++) {
    const n = m * d;
    if (Math.abs(n - Math.round(n)) < 1e-9) {
      return `${sinal}\\frac{${Math.round(n)}}{${d}}`;
    }
  }
  // Fora do alcance dos denominadores pequenos: o gerador não deveria chegar aqui.
  return `${sinal}${formatBR(m, 2).replace(',', '{,}')}`;
}

/** Descrição do coeficiente em palavras, para o texto corrido e para o leitor de tela. */
function racionalEmPalavras(x: number): string {
  const sinal = x < 0 ? '−' : '';
  const m = Math.abs(x);
  if (Number.isInteger(m)) return `${sinal}${m}`;
  for (let d = 2; d <= 6; d++) {
    const n = m * d;
    if (Math.abs(n - Math.round(n)) < 1e-9) return `${sinal}${Math.round(n)}/${d}`;
  }
  return `${sinal}${formatBR(m, 2)}`;
}

/** "3 unidades", "1 unidade", "1/2 de unidade" — a fração se lê "de unidade". */
function unidades(x: number): string {
  return Number.isInteger(x) ? String(conta(x, 'unidade')) : `${racionalEmPalavras(x)} de unidade`;
}

function leiEmTex(a: number, b: number): string {
  const coefA = a === 1 ? '' : a === -1 ? '-' : racionalTex(a);
  const sinalB = b >= 0 ? '+' : '-';
  const absB = formatBR(Math.abs(b), 0);
  return `f(x) = ${coefA}x ${sinalB} ${absB}`;
}

// ---------------------------------------------------------------------------
// Explicações: o que o gráfico escolhido tem de diferente, com os números da variante
// ---------------------------------------------------------------------------

const num = (x: number): string => racionalEmPalavras(x);
const ponto = (x: number, y: number): string => `(${num(x)}, ${num(y)})`;
const sentido = (a: number): string => (a > 0 ? 'sobe' : 'desce');

const EXPLICA: Explicacoes<Params, Reta> = {
  [FUN_SINAL_ANGULAR]: (p, r) => [
    txt(
      `O gráfico que você escolheu corta o eixo y no ponto certo, ${ponto(0, p.b)}, mas a ` +
        `reta ${sentido(r.a)} da esquerda para a direita. `,
    ),
    txt('Em '),
    tex(leiEmTex(p.a, p.b)),
    txt(
      `, o número que multiplica x é ${num(p.a)}, ${p.a < 0 ? 'negativo' : 'positivo'}: a ` +
        `cada 1 unidade para a direita, a reta ${sentido(p.a)} ${num(Math.abs(p.a))}.`,
    ),
  ],
  [FUN_INTERCEPTO_EM_X]: (p) => [
    txt(
      `O gráfico que você escolheu tem a inclinação certa, mas corta o eixo x no ponto ` +
        `${ponto(p.b, 0)}: o termo independente, ${num(p.b)}, foi lido como o lugar em que a ` +
        'reta cruza o eixo x. ',
    ),
    txt(`O termo independente diz onde a reta corta o eixo y — no ponto ${ponto(0, p.b)}.`),
  ],
  [FUN_ANGULAR_UM]: (p, r) => [
    txt(
      `O gráfico que você escolheu corta o eixo y no ponto certo, ${ponto(0, p.b)}, e ` +
        `${sentido(r.a)} como deveria — mas a sua inclinação é ${num(r.a)}: a cada 1 unidade ` +
        `para a direita, ${sentido(r.a)} exatamente 1. `,
    ),
    txt('Em '),
    tex(leiEmTex(p.a, p.b)),
    txt(
      `, o número que multiplica x é ${num(p.a)}: a reta ${sentido(p.a)} ` +
        `${num(Math.abs(p.a))} a cada 1 unidade para a direita.`,
    ),
  ],
  [FUN_PELA_ORIGEM]: (p) => [
    txt('O gráfico que você escolheu tem a inclinação certa, mas passa pela origem, (0, 0). '),
    txt(
      `O termo ${p.b > 0 ? '+' : '−'} ${num(Math.abs(p.b))} da função desloca a reta ` +
        `${p.b > 0 ? 'para cima' : 'para baixo'}: ela corta o eixo y em ${ponto(0, p.b)}.`,
    ),
  ],
  [FUN_TROCA_A_B]: (p) => [
    txt(
      `No gráfico que você escolheu, a reta corta o eixo y em ${ponto(0, p.a)} e tem ` +
        `inclinação ${num(p.b)}: os papéis dos dois números foram trocados. `,
    ),
    txt('Em '),
    tex(leiEmTex(p.a, p.b)),
    txt(
      `, o ${num(p.a)} que multiplica x é a inclinação, e o ${num(p.b)} é onde a reta ` +
        'corta o eixo y.',
    ),
  ],
  [FUN_REFLETE_EIXO_X]: (_p, r) => [
    txt(
      `O gráfico que você escolheu tem os dois sinais trocados: corta o eixo y em ` +
        `${ponto(0, r.b)} e ${sentido(r.a)} da esquerda para a direita. É o gráfico de `,
    ),
    tex(leiEmTex(r.a, r.b)),
    txt(' — a reta pedida, refletida no eixo x.'),
  ],
};

export const funcaoGrafico: ItemTemplate<Params, Reta> = {
  id: templateId('h1-08-01-funcao-grafico'),
  // v4: numeração só na origem e nas marcas extremas; as demais marcas ficam sem
  // número, para a figura não poluir. A figura que o aluno vê mudou.
  // v5: malha quadriculada no lugar da malha de pontos.
  version: 5,
  taskModel: taskModelCode('H1.8.01'),
  skills: [skillCode('H1.8')],
  axis: 'I',
  level: 'N1',
  title: 'Corresponder a lei da função afim ao seu gráfico',
  responseModel: '3PL',

  difficultySources: ['steps.1'],

  params: drawParams,
  solve: solveKey,
  buggy: comExplicacoes(BUGGY, EXPLICA),
  equals: mesmaReta,

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // O catálogo aponta como caso difícil a reta com os dois coeficientes negativos.
    if (p.a < 0 && p.b < 0) fontes.push('distractor-proximity.high');
    if (Math.abs(p.a) < 1) fontes.push('non-round-numbers');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const interaction = assembleSelectOne<Reta>({
      structure: 'E6',
      key,
      distractors,
      // Texto visível VAZIO: a alternativa é o gráfico. Ver a nota no topo do arquivo.
      render: () => [],
      // A identidade da alternativa vive no rawValue — é o que mantém as cinco
      // distintas para as invariantes, sem aparecer no payload do aluno.
      raw: (r) => `a=${r.a};b=${r.b}`,
      figure: (r) =>
        afimScene(r.a, r.b, {
          span: p.span,
          step: 1,
          title: 'Gráfico de uma função afim',
          desc: describeAfim(r.a, r.b),
        }),
      rng,
    });

    return {
      stem: [txt('Considere a função afim definida por '), tex(leiEmTex(p.a, p.b)), txt('.')],
      comando: [txt('Qual dos gráficos abaixo representa essa função?')],
      // Sem estímulo separado: as alternativas SÃO as figuras (estrutura E6).
      stimuli: [],
      interaction,
      dicas: [
        [txt('Comece pelo ponto em que a reta corta o eixo y: ali, x = 0.')],
        [
          txt('Calcule f(0) na lei '),
          tex(leiEmTex(p.a, p.b)),
          txt(' e procure os gráficos que passam pelo ponto (0, f(0)).'),
        ],
        [
          txt(
            `Entre esses, olhe o sentido: o número que multiplica x é ${p.a > 0 ? 'positivo' : 'negativo'}, ` +
              'e ele diz quanto a reta sobe ou desce a cada 1 unidade para a direita.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `O coeficiente linear é ${num(p.b)}, então a reta corta o eixo y em ` +
            `${ponto(0, p.b)}. `,
        ),
        // Redação do autor (2026-09-29): "a cada 1 unidade para a direita, sobe 3" não
        // dizia O QUE anda para a direita, nem em que se mede a subida.
        txt(
          `O coeficiente angular é ${racionalEmPalavras(p.a)}, então a reta é ` +
            `${p.a > 0 ? 'crescente' : 'decrescente'} e, a cada 1 unidade que um ponto dessa reta ` +
            `se desloca para a direita, ${p.a > 0 ? 'sobe' : 'desce'} ${unidades(Math.abs(p.a))}.`,
        ),
      ],
      declared: p.a < 0 && p.b < 0 ? 'dificil' : 'media',
    };
  },

  validate(built, p) {
    if (built.interaction.kind !== 'select-one') return 'interação inesperada';
    // Toda alternativa precisa ter figura: numa estrutura E6, alternativa sem gráfico
    // é alternativa em branco.
    if (!built.interaction.alternatives.every((a) => a.figure)) {
      return 'há alternativa sem figura numa estrutura E6';
    }
    if (Math.abs(p.b) > p.span) return `intercepto ${p.b} fora do quadro`;
    return null;
  },
};
